import { Box, Typography } from '@mui/material';
import {
  MatchAccessibility,
  MatchPlayerRole,
  MatchPlayerStatus,
  MatchStatus,
} from '@okletsplay/core/src/modules/match/enums';
import { MatchPanelType } from 'modules/matches/enums';
import { MatchPlayer } from 'app/interfaces/entity';
import React, { FC, useMemo } from 'react';
import { usePlatformType } from 'modules/games/hooks/use-platform-type';
import { useStyles } from './OwnerActions.styles';
import { PlatformType } from '@okletsplay/core';
import { MatchLeaderboardModalActions } from '../../MatchLeaderboardModal/MatchLeaderboardModalActions';

interface OwnerActionsProps {
  matchId: string;
  players?: MatchPlayer[];
  accessibility?: MatchAccessibility;
  matchStatus?: MatchStatus;
  platformType: PlatformType;
  isJoinApproveRequired?: boolean;
  openMatchPanel: (panel: MatchPanelType) => void;
}

export const OwnerActions: FC<OwnerActionsProps> = ({
  matchId,
  openMatchPanel,
  accessibility,
  matchStatus,
  platformType,
  players = [],
  isJoinApproveRequired,
}) => {
  const { classes } = useStyles();
  const isAsyncGame = usePlatformType(platformType);

  const inviteRequestsCount = useMemo(
    () => players?.filter((player) => player?.status === MatchPlayerStatus.Invited)?.length ?? 0,
    [players]
  );

  const isPublicMatchWithInvite = useMemo(
    () =>
      accessibility === MatchAccessibility.Public &&
      players?.find((player) => player?.status === MatchPlayerStatus.Invited),
    [players, accessibility]
  );

  const ownerPlayer = useMemo(
    () => players?.find((player) => player.role === MatchPlayerRole.Owner),
    [players]
  );

  const playActions = useMemo(() => {
    if (isAsyncGame && ownerPlayer?.status === MatchPlayerStatus.Ready) {
      return (
        <MatchLeaderboardModalActions
          matchId={matchId}
          playerStatus={ownerPlayer?.status}
          hideForfeit
        />
      );
    }

    return null;
  }, [isAsyncGame, matchId, ownerPlayer?.status]);

  const ownerActions = {
    [MatchAccessibility.Public]: (
      <>
        <Typography className={classes.text} variant="body1">
          {isJoinApproveRequired ? (
            <>
              Accept{' '}
              <Typography
                onClick={() => openMatchPanel(MatchPanelType.JoinRequests)}
                className={classes.textAction}
                variant="body2"
                component="span"
              >
                join requests
              </Typography>{' '}
              to build your match.
            </>
          ) : (
            'Waiting on players to join match.'
          )}
        </Typography>
      </>
    ),
    [MatchAccessibility.Private]: (
      <>
        <Typography className={classes.text} variant="body1">
          Waiting on{' '}
          <Typography
            onClick={() => openMatchPanel(MatchPanelType.TeamSelection)}
            className={classes.textAction}
            variant="body2"
            component={'span'}
          >
            {`${inviteRequestsCount} players`}
          </Typography>{' '}
          to accept invites.
        </Typography>
      </>
    ),
  };

  if (!accessibility) {
    return null;
  }

  if (matchStatus === MatchStatus.Declined) {
    return (
      <Typography className={classes.text} variant="body1">
        This match has been declined.
      </Typography>
    );
  }

  return (
    <>
      {isPublicMatchWithInvite
        ? ownerActions[MatchAccessibility.Private]
        : ownerActions[accessibility]}

      {playActions ? (
        <Box mt={1} sx={{ width: '100%' }}>
          {playActions}
        </Box>
      ) : null}
    </>
  );
};
